import { useState, useEffect } from "react";

export function ImcGauge({ imc, classification, animate }) {
  const imcVal = parseFloat(imc) || 0;
  const clamped = Math.max(15, Math.min(40, imcVal));

  // Gauge geometry
  const cx = 110, cy = 105, r = 80, sw = 22;
  const fullC = 2 * Math.PI * r;   // full circumference
  const halfC = Math.PI * r;       // semicircle length

  // Needle angle: IMC 15 → 180° (left), IMC 40 → 0° (right)
  const targetDeg = 180 - ((clamped - 15) / 25) * 180;
  const [needleDeg, setNeedleDeg] = useState(180);

  useEffect(() => {
    if (animate) {
      const t = setTimeout(() => setNeedleDeg(targetDeg), 80);
      return () => clearTimeout(t);
    } else {
      setNeedleDeg(180);
    }
  }, [animate, targetDeg]);

  // Zones: each mapped to a portion of the semicircle
  const zones = [
    { from: 15, to: 18.5, color: "#F9A01B" },   // Bajo peso
    { from: 18.5, to: 24.9, color: "#2A9D8F" },  // Normal
    { from: 24.9, to: 29.9, color: "#F9A01B" },  // Sobrepeso
    { from: 29.9, to: 40, color: "#C0453A" },     // Obesidad
  ];

  let cumOffset = 0;
  const zoneData = zones.map((z) => {
    const pct = (z.to - z.from) / 25;
    const len = pct * halfC;
    const item = { ...z, len, offset: cumOffset };
    cumOffset += len;
    return item;
  });

  // Needle tip position
  const needleRad = (needleDeg * Math.PI) / 180;
  const needleLen = r - sw / 2 - 8;
  const nx = cx + needleLen * Math.cos(needleRad);
  const ny = cy - needleLen * Math.sin(needleRad);

  const cls = classification;
  const needleColor = cls?.color || "#2D2A24";

  return (
    <div className="flex flex-col items-center" data-testid="imc-gauge">
      <svg viewBox="0 0 220 125" style={{ width: "100%", maxWidth: 220 }}>
        {/* Zone arcs using stroke-dasharray on circles */}
        {zoneData.map((z, i) => (
          <circle
            key={i}
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={z.color}
            strokeWidth={sw}
            strokeLinecap="butt"
            strokeDasharray={`${z.len} ${fullC}`}
            strokeDashoffset={-z.offset}
            transform={`rotate(180, ${cx}, ${cy})`}
            opacity="0.75"
          />
        ))}

        {/* Needle line */}
        <line
          x1={cx} y1={cy} x2={nx} y2={ny}
          stroke="#2D2A24" strokeWidth="2.5" strokeLinecap="round"
          style={{ transition: "all 1s cubic-bezier(0.4, 0, 0.2, 1)" }}
        />
        {/* Needle tip dot */}
        <circle
          cx={nx} cy={ny} r="4"
          fill={needleColor}
          style={{ transition: "all 1s cubic-bezier(0.4, 0, 0.2, 1)" }}
        />
        {/* Center pivot */}
        <circle cx={cx} cy={cy} r="5" fill="#2D2A24" />

        {/* Zone labels below the arc */}
        <text x={cx - r - 2} y={cy + 16} textAnchor="middle" fontSize="8" fill="#B5A998" fontFamily="Inter, sans-serif">15</text>
        <text x={cx + r + 2} y={cy + 16} textAnchor="middle" fontSize="8" fill="#B5A998" fontFamily="Inter, sans-serif">40</text>
      </svg>

      {/* Value display */}
      <div className="text-center -mt-1">
        <div style={{ fontSize: 28, fontWeight: 800, color: "#2D2A24", lineHeight: 1 }}>
          {imcVal.toFixed(1)}
        </div>
        {cls && (
          <span
            className="inline-block mt-1 text-xs font-bold px-2.5 py-0.5 rounded-lg"
            style={{ background: `${cls.color}15`, color: cls.color }}
          >
            {cls.label}
          </span>
        )}
      </div>
    </div>
  );
}
